import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { toast } from "react-toastify";

import InputField from "./InputField";
import SelectField from "./SelectField";

const ModelForm = ({ model, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    marca: "",
    categoria: "",
    img: "",
    fabricante: "",
    nuevaMarca: "",
  });

  const [marcas, setMarcas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [isNuevaMarca, setIsNuevaMarca] = useState(false);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL;

  // 🔹 Obtener marcas
  const fetchMarcas = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/marcas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMarcas(res.data || []);
    } catch (error) {
      toast.error("No se pudieron cargar las marcas");
    }
  }, [API_URL, token]);

  // 🔹 Obtener categorías
  const fetchCategorias = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/cat`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategorias(res.data || []);
    } catch (error) {
      toast.error("No se pudieron cargar las categorías");
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchMarcas();
    fetchCategorias();
  }, [fetchMarcas, fetchCategorias]);

  // 🔹 Editar modelo existente
  useEffect(() => {
    if (model) {
      setFormData({
        name: model.name || "",
        marca:
          typeof model.marca === "string"
            ? model.marca
            : model.marca?._id || "",
        categoria:
          typeof model.categoria === "string"
            ? model.categoria
            : model.categoria?._id || "",
        img: model.img || "",
        fabricante: model.fabricante || "",
        nuevaMarca: "",
      });
      setIsNuevaMarca(false);
    }
  }, [model]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "marca") {
      if (value === "__nueva__") {
        setIsNuevaMarca(true);
        setFormData((s) => ({ ...s, marca: "", nuevaMarca: "" }));
      } else {
        setIsNuevaMarca(false);
        setFormData((s) => ({ ...s, marca: value, nuevaMarca: "" }));
      }
    } else {
      setFormData((s) => ({ ...s, [name]: value }));
    }
  };

  // 🔹 Manejo de imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((s) => ({ ...s, img: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  // 🔹 Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let marcaSeleccionada = formData.marca;

      if (isNuevaMarca && formData.nuevaMarca.trim()) {
        const res = await axios.post(
          `${API_URL}/marcas`,
          { name: formData.nuevaMarca },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        marcaSeleccionada = res.data._id;
        toast.success("Marca creada correctamente ✅");
      }

      await onSave({
        name: formData.name,
        marca: marcaSeleccionada,
        categoria: formData.categoria,
        img: formData.img,
        fabricante: formData.fabricante,
      });
    } catch (error) {
      toast.error("Error guardando el modelo");
    } finally {
      setLoading(false);
    }
  };

  const marcasOptions = [
    ...marcas,
    { _id: "__nueva__", name: "➕ Crear nueva marca" },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      {/* Modal responsive con scroll */}
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {model ? "Editar modelo" : "Nuevo modelo"}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <InputField
                label="Modelo *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Modelo"
                required
              />

              <SelectField
                label="Categoría *"
                name="categoria"
                value={formData.categoria}
                options={categorias}
                valueKey="_id"
                displayFn={(c) => c.name}
                placeholder="Seleccionar categoría..."
                onChange={handleChange}
              />

              <SelectField
                label="Marca *"
                name="marca"
                value={isNuevaMarca ? "__nueva__" : formData.marca}
                options={marcasOptions}
                valueKey="_id"
                displayFn={(m) => m.name}
                placeholder="Seleccionar marca..."
                onChange={handleChange}
              />

              {isNuevaMarca && (
                <InputField
                  label="Nueva marca *"
                  name="nuevaMarca"
                  value={formData.nuevaMarca}
                  onChange={handleChange}
                  placeholder="Nombre de la nueva marca"
                  required
                />
              )}

              <InputField
                label="Fabricante"
                name="fabricante"
                value={formData.fabricante}
                onChange={handleChange}
                placeholder="Fabricante"
              />

              {/* Imagen */}
              {formData.img && (
                <div>
                  <p className="text-sm font-medium mb-1">Imagen actual</p>
                  <img
                    src={formData.img}
                    alt="Modelo"
                    className="border rounded"
                    style={{
                      width: "200px",
                      height: "200px",
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Imagen</label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full border p-2 rounded"
                  onChange={handleImageChange}
                />
              </div>

              {/* Acciones */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={onClose}
                  type="button"
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  {loading ? "Guardando..." : model ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ModelForm;
